import {DataGrid, GridFilterModel, GridRowSelectionModel, GridSortModel, useGridApiRef} from "@mui/x-data-grid";
import React, {useEffect, useState} from "react";
import {useListContext} from "../../contexts/ListContext";

import {useTheCellMapContext} from "../../contexts/TheCellMapContext";
import {useEntrezMap} from "../../hooks/useEntrezMap";
import {useFilteredTableData} from "../../hooks/useFilteredTableData";
import {useGeneRefs} from "../../hooks/useGeneRefs";
import {usePostListScores} from "../../hooks/useJsonLoader";
import {DataType} from "../../types/enums";
import {Scores} from "../../types/list";
import {useQGiColumns} from "./ListTableColumns";

const ListQGIScores = () => {
  const {currentGene, selectedScreen} = useTheCellMapContext();
  const {listState, setSelectedRowModal, setDisplayedTableData} = useListContext();
  const apiRef = useGridApiRef();

  const {negativeColumns, positiveColumns} = useQGiColumns(apiRef);
  const [filterModel, setFilterModel] = useState<GridFilterModel>();

  // Define the initial sortModel for each dataType
  const negativeSortModel: GridSortModel = [{field: "qgi", sort: "asc"}];
  const positiveSortModel: GridSortModel = [{field: "qgi", sort: "desc"}];
  const initialSortModel = listState.dataType === DataType.Negative
    ? negativeSortModel
    : positiveSortModel;

  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const entrezMap = useEntrezMap();
  const geneRefs = useGeneRefs();
  const postScores = usePostListScores();
  const [data, setData] = useState<Scores | undefined>();

  // Reset sorting when dataType changes
  useEffect(() => {
    setSortModel(
      listState.dataType === DataType.Negative
        ? negativeSortModel
        : positiveSortModel
    );
  }, [listState.dataType]);

  // Handler to allow only single-column sorting
  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setSortModel([model[model.length - 1]]);
    } else {
      setSortModel([]);
    }
  };

  const dataGridProps =
    listState.dataType === DataType.Negative
      ? {
        columns: negativeColumns,
      }
      : {
        columns: positiveColumns,
      };

  useFilteredTableData({data, columnDef: dataGridProps.columns}, filterModel);

  useEffect(() => {
    if (!data || selectionModel.length === 0) return; // We get ref only when data is loaded and grid is ready
    setSelectedRowModal(apiRef.current.getRow(selectionModel[0]));

    return () => {
      setSelectedRowModal(null); // Reset selection when component unmounts so we don't mix up data
    }
  }, [selectionModel, data]);

  useEffect(() => {
    if (!data || !apiRef.current) return;

    const rowId = apiRef.current.getRowIdFromRowIndex(0);
    if (rowId) {
      // Set selected row to the first row if the sort model or data changes
      setSelectionModel([rowId]);
    }
  }, [sortModel, data, apiRef, setSelectionModel]);

  useEffect(() => {
    setData(undefined);

    if (!currentGene || !entrezMap || !geneRefs) return;

    postScores(
      {
        query_type: listState.queryType,
        query: currentGene?.value,
        screen_id: selectedScreen || "",
      },
      false
    ).then(results => {
      if (!results) return;

      /* Pre-filter the scores based on the selected dataType, to avoid having to filter the data in the grid */
      const filtered: Scores = {
        scores: results.scores.filter(
          value => listState.dataType === DataType.Negative ? value.qgi < 0 : value.qgi > 0)
      };

      filtered.scores.forEach(score => {
        let entrezID = +score.gene_entrez;
        let symbol = entrezMap[entrezID].s;
        let useTwoNames = symbol !== score.gene;
        score.library_cell = {
          entrez: entrezID,
          symbol: symbol,
          display: useTwoNames ? symbol : score.gene,
          alternate: useTwoNames ? score.gene : null,
          screen: null,
          refs: geneRefs[entrezID]
        }

        const queryGene = score.screen.split(/\s+/);
        const entrezIDs = score.screen_entrez.split(",").map(entrezID => +entrezID);
        let symbolName = entrezIDs.map(e => entrezMap[e].s).join("/");
        useTwoNames = symbolName !== queryGene[0];

        /** Define single entrez and symbol for the search links. Take double mutants and current gene into account */

        entrezID = entrezIDs[0]; // Take first entrez id
        if (currentGene && entrezIDs.indexOf(currentGene.value) !== -1) {
          // This (double) mutant is the seached gene. Use the searched id instead
          entrezID = currentGene.value;
        }

        // Use above defined entrez as our main symbol for links
        symbolName = entrezMap[entrezID].s;

        score.screen_cell = {
          entrez: entrezID,
          symbol: symbolName,
          display: useTwoNames ? `${symbolName} — ${queryGene[1]}` : score.screen.split(' ').join(' — '),
          alternate: useTwoNames ? score.screen.split(' ').join(' — ') : null,
          screen: queryGene[1],
          refs: geneRefs[entrezID]
        }
      });

      setData(filtered);
      setDisplayedTableData(filtered);
      apiRef.current?.setFilterModel({ items: [] }); // Clear filters when data changes
    })

    return () => {
      setData(undefined);
      setSelectedRowModal(null);
      setDisplayedTableData(null);
    }
  }, [entrezMap, currentGene, geneRefs, listState.dataType, listState.queryType, selectedScreen]);

  return (
    <DataGrid
      rows={data ? data.scores : []}
      autoHeight={false}
      className="border-0"
      disableColumnMenu  // To reduce clutter in header
      disableColumnFilter  // We take care of filtering manually via ListHeaderFiltered
      {...dataGridProps}
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      sortingOrder={['asc', 'desc']}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={setSelectionModel}
      onFilterModelChange={setFilterModel}
      apiRef={apiRef}
      localeText={{
        noRowsLabel: !currentGene ? 'Search genes to view interactions.'
          : data ? 'No significant genetic interaction data.' : 'Loading...',
      }}
      getRowClassName={(params) => params.row.is_suppressor === 1 ? 'pos-suppressor-row' : ''}
    />
  )
}

export default ListQGIScores;